import React, { useState } from 'react'
import {
  Button,
  HiddenLocationIndicator,
  IconButton,
  PrettyNumber,
  NewGridTable,
  IGridTableColumn,
  IGridTableCell,
  Skeleton,
} from '@ui'
import { BMTableBuilding, BMTableFloor } from '@api'
import { Tag } from '@blueprintjs/core'
import Toolbar from './Toolbar'
import { NavigateFn } from '@reach/router'
// import moment from 'moment'
import NewFloorDialog from './NewFloorDialog'
import { legacyMode } from 'App'
import { navigateToAphrodite } from '@utils/navigateToAphrodite'
import { UseBuildingsHook } from './hooks/useBuildings'
import { useReload } from '@hooks/reloadContext'
import track from '@track'
import Event from '@track/Event'
import Permissions from '@permissions/Permissions'
import { PermissionsType, IPermissionsOnBuilding } from '@permissions/types'
import { DateUtil, pluralize, sqFtToSqMeters } from '@utils'
import { useUser } from '@hooks'

interface IProps {
  buildings: UseBuildingsHook
  navigate?: NavigateFn
}

export default function TableView({ buildings, navigate }: IProps) {
  const [newFloorBuildingId, setNewFloorBuildingId] = useState<null | string>(null)
  const [listView, setListview] = useState(false)
  const [showFilters, setShowFilters] = useState(true)
  const toggleListView = () => setListview(!listView)
  const reload = useReload()

  const buildingColumns: IGridTableColumn<BMTableBuilding>[] = [
    {
      label: 'Building Name',
      Cell: ({ skeleton, cell }) =>
        skeleton ? (
          <Skeleton withButton />
        ) : (
          <div className="flex items-center">
            <div
              className="ml-2 font-medium text-base hover:underline hover:text-link cursor-pointer"
              onClick={() => {
                navigate?.(`${window.location.pathname}?buildingId=${cell?.data.id}`)
                track.buildings({ eventName: Event.ROW_CLICK })
              }}
            >
              {cell?.data.name}
            </div>
            {(!cell?.data.floors ||
              cell.data.floors.reduce<boolean>((agg, curr) => curr.hidden && agg, true)) && (
              <span style={{ marginLeft: 10 }}>
                <HiddenLocationIndicator />
              </span>
            )}
            {cell?.data.closedAt && DateUtil.utc(cell.data.closedAt).isBefore(DateUtil()) && (
              <Tag round minimal style={{ marginLeft: 10 }}>
                Inactive
              </Tag>
            )}
          </div>
        ),
      sort: 'name',
    },
    {
      label: 'City',
      sort: 'city',
      Cell: ({ skeleton, cell }) => (skeleton ? <Skeleton /> : <span>{cell?.data.city}</span>),
    },
    {
      label: 'Address',
      Cell: ({ skeleton, cell }) => (skeleton ? <Skeleton /> : <span>{cell?.data.address}</span>),
    },
    {
      label: 'HR Location',
      sort: 'geoLocationId',
      Cell: ({ skeleton, cell }) =>
        skeleton ? <Skeleton /> : <span>{cell?.data.geoLocation.location}</span>,
    },
    {
      label: 'Building Type',
      sort: 'buildingTypeDisplayName',
      Cell: ({ skeleton, cell }) =>
        skeleton ? <Skeleton /> : <span>{cell?.data?.buildingType?.displayName || '-'}</span>,
    },
    {
      label: '',
      Cell: ({ skeleton, cell }) =>
        skeleton ? (
          <Skeleton />
        ) : (
          <Permissions type={PermissionsType.BUILDING} on={cell?.data}>
            {(permissions) =>
              (permissions as IPermissionsOnBuilding).canCreateFloor ? (
                <Button
                  text="Add Floor"
                  iconName="plus"
                  onClick={() => {
                    setNewFloorBuildingId(cell?.data.id ?? '')

                    track.buildings({
                      eventName: Event.NEW_RECORD_DIALOG,
                      props: {
                        component: 'Floor',
                      },
                    })
                  }}
                />
              ) : null
            }
          </Permissions>
        ),
    },
  ]

  const formattedB = formatBuildings(buildings.items, navigate)

  return (
    <>
      <Toolbar
        buildings={buildings}
        navigate={navigate}
        toggleListView={toggleListView}
        listView={listView}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
      />
      <NewGridTable<BMTableBuilding>
        status={buildings.status}
        columns={buildingColumns}
        data={formattedB}
        page={{
          hasNextPage: buildings.pageInfo.hasNextPage,
          endCursor: buildings.pageInfo.endCursor,
          onNextPage: buildings.nextPage,
        }}
        sort={{
          column: buildings.sort.column,
          order: buildings.sort.order,
          onSort: (column: string) => {
            buildings.selection.clear()
            buildings.sortBy(column)
            track.buildings({
              eventName: Event.SORT,
              props: { columnName: column },
            })
          },
        }}
        headerTop={showFilters ? 183 : 70}
      />
      {newFloorBuildingId && (
        <NewFloorDialog
          isOpen={!!newFloorBuildingId}
          buildingId={newFloorBuildingId}
          onClose={() => setNewFloorBuildingId(null)}
          reload={reload.buildings}
          onSuccess={(floor, shouldCallback) => {
            reload.buildings()
            if (shouldCallback) {
              setNewFloorBuildingId(null)
              navigate && navigate(`${window.location.pathname}?floorId=${floor.id}`)
            }
          }}
        />
      )}
    </>
  )
}

const formatBuildings = (items: IGridTableCell<BMTableBuilding>[], navigate?: NavigateFn) => {
  return items.map((b) => ({
    ...b,
    expandToggle: b.data.floors.length
      ? `${b.data.floors.length} ${pluralize(b.data.floors.length, 'Floor')}`
      : undefined,
    child: b.data.floors.length ? (
      <BuildingFloors floors={b.data.floors.map((f) => ({ data: f }))} navigate={navigate} />
    ) : undefined,
  }))
}

function BuildingFloors({
  floors,
  navigate,
}: {
  floors: IGridTableCell<BMTableFloor>[]
  navigate?: NavigateFn
}) {
  const { user } = useUser()
  const floorColumns: IGridTableColumn<BMTableFloor>[] = [
    {
      label: 'Floor Name',
      cellStyle: {
        width: '100%',
      },
      Cell: ({ cell }) => (
        <div className="flex items-center">
          <div
            className="ml-2 font-medium text-base hover:underline hover:text-link cursor-pointer"
            onClick={() => {
              navigate?.(`${window.location.pathname}?floorId=${cell?.data.id}`)
              track.buildings({ eventName: Event.ROW_CLICK })
            }}
          >
            {cell?.data.name}
          </div>
          {cell?.data.hidden && (
            <span style={{ marginLeft: 10 }}>
              <HiddenLocationIndicator floor />
            </span>
          )}
        </div>
      ),
    },
    {
      label: 'Floor no.',
      alignRight: true,
      Cell: ({ cell }) => <span>{cell?.data.story}</span>,
    },
    {
      label: 'Max Capacity',
      alignRight: true,
      Cell: ({ cell }) => <PrettyNumber value={cell?.data.capacity ?? 0} />,
    },
    {
      label: 'Usable Area',
      alignRight: true,
      Cell: ({ cell }) => (
        <PrettyNumber
          value={
            user?.context.units === 'sqm'
              ? sqFtToSqMeters(cell?.data.usableArea ?? 0)
              : cell?.data.usableArea ?? 0
          }
        />
      ),
    },
    {
      label: 'Rentable Area',
      alignRight: true,
      Cell: ({ cell }) => (
        <PrettyNumber
          value={
            user?.context.units === 'sqm'
              ? sqFtToSqMeters(cell?.data.rentableArea ?? 0)
              : cell?.data.rentableArea ?? 0
          }
        />
      ),
    },
    {
      label: 'Floor Plan (last uploaded)',
      Cell: ({ cell }) =>
        cell?.data.assetVersion && cell?.data.assetVersion.background ? (
          <div className="flex items-center">
            <IconButton
              minimal
              iconName="map-marked"
              tooltip="Go to Floor Plan"
              style={{ marginRight: 10 }}
              onClick={() => {
                legacyMode
                  ? navigateToAphrodite(`floor_map&floorId=${cell?.data.id}`)
                  : navigate && navigate(`/floor_plan/${cell?.data.id}`)

                track.buildings({
                  eventName: Event.INLINE_ACTION,
                  props: {
                    actionName: 'Go To Floor Plan',
                    actionValue: null,
                  },
                })
              }}
            />
            <span style={{ color: '#636363', fontSize: 12 }}>
              ({DateUtil(cell?.data.assetVersion.background.updatedAt).format('MMM d, yyyy')})
            </span>
          </div>
        ) : (
          <span>-</span>
        ),
    },
  ]

  return (
    <NewGridTable<BMTableFloor>
      status="idle"
      sticky={false}
      data={floors}
      minCellWidth="auto"
      columns={floorColumns}
      noDataMessage="No Floors"
      childTable={true}
    />
  )
}
